import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getAxiosConfig } from '../utils/http';

const BASE = '/api/aliexpress/service-clients';

export const listServiceClients = createAsyncThunk(
  'serviceClient/list',
  async (_, { getState, rejectWithValue }) => {
    try {
      const config = getAxiosConfig(getState(), { withCredentials: true });
      const { data } = await axios.get(BASE, config);
      return data.clients;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Returns { client, key } - the plaintext key is only ever present in this
// one response and is never stored in Redux state; callers must capture it
// via .unwrap() and hold it in local component state only.
export const createServiceClient = createAsyncThunk(
  'serviceClient/create',
  async (payload, { getState, dispatch, rejectWithValue }) => {
    try {
      const config = getAxiosConfig(getState(), { withCredentials: true });
      const { data } = await axios.post(BASE, payload, config);
      dispatch(listServiceClients());
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const rotateServiceClient = createAsyncThunk(
  'serviceClient/rotate',
  async ({ id, expiresAt }, { getState, dispatch, rejectWithValue }) => {
    try {
      const config = getAxiosConfig(getState(), { withCredentials: true });
      const { data } = await axios.post(`${BASE}/${id}/rotate`, { expiresAt }, config);
      dispatch(listServiceClients());
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const revokeServiceClient = createAsyncThunk(
  'serviceClient/revoke',
  async (id, { getState, dispatch, rejectWithValue }) => {
    try {
      const config = getAxiosConfig(getState(), { withCredentials: true });
      const { data } = await axios.delete(`${BASE}/${id}`, config);
      dispatch(listServiceClients());
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  clients: [],
  loading: false,
  error: null,
  creating: false,
  createError: null,
  rotating: false,
  rotateError: null,
  revoking: false,
  revokeError: null,
};

const serviceClientSlice = createSlice({
  name: 'serviceClient',
  initialState,
  reducers: {
    resetServiceClientErrors: (state) => {
      state.error = null;
      state.createError = null;
      state.rotateError = null;
      state.revokeError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listServiceClients.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(listServiceClients.fulfilled, (state, action) => { state.loading = false; state.clients = action.payload; })
      .addCase(listServiceClients.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(createServiceClient.pending, (state) => { state.creating = true; state.createError = null; })
      .addCase(createServiceClient.fulfilled, (state) => { state.creating = false; })
      .addCase(createServiceClient.rejected, (state, action) => { state.creating = false; state.createError = action.payload; })

      .addCase(rotateServiceClient.pending, (state) => { state.rotating = true; state.rotateError = null; })
      .addCase(rotateServiceClient.fulfilled, (state) => { state.rotating = false; })
      .addCase(rotateServiceClient.rejected, (state, action) => { state.rotating = false; state.rotateError = action.payload; })

      .addCase(revokeServiceClient.pending, (state) => { state.revoking = true; state.revokeError = null; })
      .addCase(revokeServiceClient.fulfilled, (state) => { state.revoking = false; })
      .addCase(revokeServiceClient.rejected, (state, action) => { state.revoking = false; state.revokeError = action.payload; });
  },
});

export const { resetServiceClientErrors } = serviceClientSlice.actions;
export default serviceClientSlice.reducer;
